import { CheckCircle2, CircleDashed, CircleHelp, Clipboard, Loader, OctagonAlert, Send, SkipForward, SquareSplitHorizontal } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import type { PostingActions, PostingPhase, PostingVm } from '../model/viewModels'

/**
 * Раздел «Проведение». Весь экран существует ради текста ошибки 1С (ADR-8):
 * он показывается дословно у шага и документа. Состояния различаются словом,
 * значком и рамкой: «исход неизвестен» и «проведена частично» — отдельные, и
 * ни одно не повторяет POST само. «Проверить статус» ЧИТАЕТ журнал саги.
 */
const PHASES: Record<PostingPhase, { icon: ReactNode; title: string; text: string }> = {
  'not-ready': {
    icon: <CircleDashed size={18} aria-hidden="true" />,
    title: 'Не готова к проведению',
    text: 'Устраните проблемы предпросмотра — тогда станет доступно «Провести».',
  },
  ready: { icon: <Send size={18} aria-hidden="true" />, title: 'Готова к проведению', text: 'Проверьте документы ниже и проведите.' },
  sending: { icon: <Loader size={18} aria-hidden="true" />, title: 'Отправляем…', text: 'Запрос проведения в пути.' },
  unknown: {
    icon: <CircleHelp size={18} aria-hidden="true" />,
    title: 'Исход неизвестен',
    text: 'Ответ на запрос проведения не получен: неизвестно, началось ли проведение. Не нажимайте «Провести» ещё раз — сначала проверьте статус: он читается из журнала проведения и ничего в 1С не отправляет.',
  },
  running: { icon: <Loader size={18} aria-hidden="true" />, title: 'Проводится', text: 'Шаги проведения выполняются; статус обновляется сам.' },
  posted: { icon: <CheckCircle2 size={18} aria-hidden="true" />, title: 'Проведена', text: 'Все документы созданы и проведены в 1С.' },
  partial: {
    icon: <SquareSplitHorizontal size={18} aria-hidden="true" />,
    title: 'Проведена частично',
    text: 'Часть документов в 1С создана или проведена, часть — нет. Повтор проведения выполнит только недостающие шаги: сделанные пропускаются по журналу.',
  },
  failed: {
    icon: <OctagonAlert size={18} aria-hidden="true" />,
    title: 'Ошибка проведения',
    text: 'Проведение остановилось с ошибкой. Текст 1С — у шага ниже.',
  },
}

const STEP_ICONS: Record<string, ReactNode> = {
  Succeeded: <CheckCircle2 size={14} aria-hidden="true" />,
  Skipped: <SkipForward size={14} aria-hidden="true" />,
  Failed: <OctagonAlert size={14} aria-hidden="true" />,
  InProgress: <Loader size={14} aria-hidden="true" />,
  Pending: <CircleDashed size={14} aria-hidden="true" />,
}

const STEP_NAMES: Record<string, string> = {
  UpdateProductName: 'Обновление наименования',
  CreateDocument: 'Создание приёмки',
  PostDocument: 'Проведение приёмки',
  CreateTransfer: 'Создание перемещения',
  PostTransfer: 'Проведение перемещения',
  WritePrice: 'Установка цены',
}

const STEP_STATUS: Record<string, string> = {
  Pending: 'ожидает',
  InProgress: 'выполняется',
  Succeeded: 'выполнено',
  Failed: 'ошибка',
  Skipped: 'пропущено (уже сделано)',
}

const DOCUMENT_KINDS: Record<string, string> = { Receipt: 'Приёмка', Transfer: 'Перемещение' }
const DOCUMENT_STATES: Record<string, string> = { Created: 'создан, не проведён', Posted: 'проведён', Failed: 'ошибка' }

export function PostingSection({ vm, actions }: { vm: PostingVm; actions: PostingActions }) {
  const phase = PHASES[vm.phase]
  const urgent = vm.phase === 'unknown' || vm.phase === 'partial' || vm.phase === 'failed'
  return (
    <div className="onec-screen">
      <div className="onec-command" data-status={urgent ? (vm.phase === 'unknown' ? 'unknown' : 'failed') : 'succeeded'} role={urgent ? 'alert' : 'status'}>
        {phase.icon}
        <div className="onec-command__body">
          <p className="onec-command__title">{phase.title}</p>
          <p className="onec-command__text">{phase.text}</p>
          <div className="onec-command__actions">
            <Button variant="primary" icon={<Send size={16} aria-hidden="true" />} onClick={actions.post} disabled={!vm.canPost}>
              {vm.phase === 'partial' || vm.phase === 'failed' ? 'Провести недостающее' : 'Провести в 1С'}
            </Button>
            <Button variant={vm.phase === 'unknown' ? 'primary' : 'secondary'} onClick={actions.checkStatus}>
              Проверить статус
            </Button>
            <Button variant="ghost" onClick={actions.reloadPreview}>
              Обновить предпросмотр
            </Button>
          </div>
        </div>
      </div>
      {vm.draftsPending && (
        <p className="onec-banner" role="status">
          Правки строк ещё сохраняются или не сохранены — проведение откроется, когда они уйдут на сервер.
        </p>
      )}
      {vm.command !== null && vm.command.status !== 'unknown' && (
        <CommandStatus command={vm.command} onDismiss={() => vm.command !== null && actions.dismiss(vm.command.key)} />
      )}

      <section className="onec-section" aria-label="Предпросмотр">
        <div className="onec-section__head">
          <strong>Что уйдёт в 1С</strong>
        </div>
        <OneCQueryState query={vm.previewQuery} emptyText="Предпросмотра нет" onRetry={actions.reloadPreview} />
        {vm.headline !== null && <p>{vm.headline}</p>}
        {vm.documents.length > 0 && (
          <div className="onec-scroll">
            <table className="onec-table">
              <thead>
                <tr>
                  <th>Документ</th>
                  <th>Склад</th>
                  <th>Организация</th>
                  <th data-numeric="true">Строк</th>
                  <th data-numeric="true">Количество</th>
                  <th data-numeric="true">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {vm.documents.map((document, index) => (
                  <tr key={`${document.kind}-${document.warehouseName}-${index}`}>
                    <td>{DOCUMENT_KINDS[document.kind] ?? document.kind}</td>
                    <td>
                      {document.sourceWarehouseName !== null ? `${document.sourceWarehouseName} → ` : ''}
                      {document.warehouseName}
                    </td>
                    <td>{document.organizationName}</td>
                    <td data-numeric="true">{document.linesCount}</td>
                    <td data-numeric="true">{document.quantity}</td>
                    <td data-numeric="true">{document.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {vm.pricesToWrite > 0 && <p>Записей цен: {vm.pricesToWrite}</p>}
        {[...vm.blockers, ...vm.problems].map((problem) => (
          <p key={problem} className="onec-problem">
            {problem}
          </p>
        ))}
        {vm.warnings.map((warning) => (
          <p key={warning} className="onec-muted">
            Предупреждение: {warning}
          </p>
        ))}
      </section>

      {vm.progress !== null && (
        <section className="onec-section" aria-label="Ход проведения">
          <div className="onec-section__head">
            <strong>Попытка №{vm.progress.attemptNumber}</strong>
            <span>
              шагов {vm.progress.done} из {vm.progress.total} · ошибок {vm.progress.failed} · пропущено {vm.progress.skipped}
            </span>
          </div>
          <ol className="onec-steps">
            {vm.progress.entries.map((entry) => (
              <li key={entry.id}>
                {STEP_ICONS[entry.status] ?? <CircleHelp size={14} aria-hidden="true" />}
                <div>
                  <div>
                    {STEP_NAMES[entry.stepKind] ?? entry.stepKind} — {STEP_STATUS[entry.status] ?? entry.status}
                    {entry.httpStatus !== null ? ` · HTTP ${entry.httpStatus}` : ''}
                  </div>
                  {entry.resourceErrorText !== null && <p className="onec-problem">{entry.resourceErrorText}</p>}
                </div>
              </li>
            ))}
          </ol>
          {vm.progress.documents.length > 0 && (
            <ul className="onec-list" aria-label="Документы в 1С">
              {vm.progress.documents.map((document, index) => (
                <li key={`${document.kind}-${document.warehouseName}-${index}`}>
                  <span>
                    {DOCUMENT_KINDS[document.kind] ?? document.kind} → {document.warehouseName}
                    {document.documentNumber !== null ? ` №${document.documentNumber}` : ''}: {DOCUMENT_STATES[document.state] ?? document.state}
                  </span>
                  {document.resourceErrorText !== null && <p className="onec-problem">{document.resourceErrorText}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <OneCQueryState query={vm.statusQuery} emptyText="" onRetry={actions.checkStatus} />

      <details className="onec-section">
        <summary>Отчёт для разбора</summary>
        <pre className="onec-report">{vm.report}</pre>
        <div className="onec-actions">
          <Button variant="secondary" icon={<Clipboard size={16} aria-hidden="true" />} onClick={actions.copyReport}>
            Скопировать отчёт
          </Button>
        </div>
      </details>
    </div>
  )
}
